import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ModalShell } from "./ModalShell";
import { SafeLogo } from "./SafeLogo";
import { StatusBadge } from "./StatusBadge";
import { createDownloadUrl } from "../api/storage";
import { InlineIcon } from "./InlineIcon";
import { canRequestChange, pendingEdit } from "../utils/credentialEdit";

function formatDate(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatMonthYear(dateVal) {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatDateRange(start, end, isCurrent) {
  const s = formatMonthYear(start);
  const e = isCurrent ? "Present" : formatMonthYear(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || "";
}

function parseMeta(metadata) {
  if (!metadata) return {};
  if (typeof metadata === "string") {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }
  return typeof metadata === "object" ? metadata : {};
}

export function ViewCredentialModal({ isOpen, onClose, credential, editLink }) {
  const [openingDoc, setOpeningDoc] = useState(false);
  const [docError, setDocError] = useState(null);

  if (!isOpen || !credential) return null;

  const c = credential;
  const meta = parseMeta(c.metadata);
  const isEducation = c.type === "EDUCATION";
  const isWork = c.type === "WORK";
  const isOther = c.type === "OTHER";

  const typeTitle = isEducation
    ? "Education Details"
    : isWork
      ? "Work Experience Details"
      : "Certification & License Details";

  const typeSubtitle = isEducation
    ? "Academic background & verified qualification record"
    : isWork
      ? "Professional employment timeline & verified experience"
      : "Professional certification, license or accreditation";

  const dateRange = formatDateRange(c.startDate, c.endDate, c.isCurrent);
  const viewRoute = isEducation
    ? `/credentials/view/education/${c.id}`
    : isWork
      ? `/credentials/view/work/${c.id}`
      : `/credentials/view/other/${c.id}`;

  const verifiedRequest = (c.verificationRequests ?? []).find(
    (r) => r.status === "VERIFIED",
  );
  const latestDecidedRequest =
    verifiedRequest ||
    (c.verificationRequests ?? []).find(
      (r) => r.decidedAt != null || r.status === "REJECTED",
    ) ||
    (c.verificationRequests ?? [])[0];

  const reqVerifier =
    latestDecidedRequest?.organisation || latestDecidedRequest?.institution;
  const verifierName =
    c.verifierName ||
    (reqVerifier
      ? [reqVerifier.name, reqVerifier.city].filter(Boolean).join(", ")
      : null) ||
    (c.expectedOrganisation || c.expectedInstitution
      ? [
          (c.expectedOrganisation || c.expectedInstitution).name,
          (c.expectedOrganisation || c.expectedInstitution).city,
        ]
          .filter(Boolean)
          .join(", ")
      : null);

  const decidedAt = c.decidedAt || latestDecidedRequest?.decidedAt;
  const formattedDecidedAt = formatDate(decidedAt);
  const decisionReason =
    c.status === "REJECTED"
      ? c.decisionReason || latestDecidedRequest?.decisionReason
      : null;
  const editPending = pendingEdit(c);
  const changeable = canRequestChange(c);

  const handleOpenCertificate = async () => {
    if (!c.certificateS3Key) return;
    setOpeningDoc(true);
    setDocError(null);
    try {
      const res = await createDownloadUrl(c.certificateS3Key);
      const url = res?.downloadUrl || res?.url || res;
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Unable to retrieve file URL.");
      }
    } catch (err) {
      setDocError(err.message || "Failed to open document.");
    } finally {
      setOpeningDoc(false);
    }
  };

  const modalContent = (
    <ModalShell
      title={typeTitle}
      subtitle={typeSubtitle}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Scrollable Body */}
        <div className="px-5 py-5 sm:px-6 sm:py-6 overflow-y-auto space-y-5 flex-1">
          {/* Top Hero Section */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 sm:p-5 shadow-xs space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                {isEducation
                  ? "Education"
                  : isWork
                    ? "Work Experience"
                    : "Certification"}
              </span>
              <div className="flex items-center gap-2">
                <StatusBadge status={c.status} size="lg" />
                <span
                  className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                    c.visibleOnShare !== false
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {c.visibleOnShare !== false
                    ? "Visible on Share Profile"
                    : "Hidden from Share"}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 leading-snug">
                {c.title}
              </h3>
            </div>

            {/* Issuer / Institution / Employer */}
            <div className="flex items-center gap-3 pt-1 border-t border-slate-200/70">
              <SafeLogo
                src={c.issuerLogoUrl}
                alt={c.issuer || "Issuer"}
                fallbackText={c.issuer ? c.issuer.slice(0, 2) : "TP"}
                imgClassName="w-10 h-10 rounded-xl object-contain border border-slate-200 bg-white p-1 shrink-0 shadow-xs"
                fallbackClassName="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 text-xs font-bold uppercase shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-slate-800 truncate">
                  {c.issuer}
                </div>
                {c.issuerDescription && (
                  <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                    {c.issuerDescription}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timeline & Metadata Grid */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-xs space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-[.06em] text-slate-400">
              Details & Attributes
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
              {/* Duration / Timeline */}
              {dateRange && (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                    Duration / Period
                  </div>
                  <div className="text-slate-800 font-semibold mt-1">
                    {dateRange}
                  </div>
                </div>
              )}

              {/* Education Specifics */}
              {isEducation && (
                <>
                  {(meta.fieldOfStudy || meta.department) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Field of Study / Department
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.fieldOfStudy || meta.department}
                      </div>
                    </div>
                  )}

                  {meta.grade && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Grade / CGPA
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.grade}
                      </div>
                    </div>
                  )}

                  {(meta.enrollmentNo ||
                    meta.registrationNo ||
                    meta.rollNumber) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Enrollment / Registration No.
                      </div>
                      <div className="text-slate-800 font-semibold mt-1 font-mono">
                        {meta.enrollmentNo ||
                          meta.registrationNo ||
                          meta.rollNumber}
                      </div>
                    </div>
                  )}

                  {meta.name && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Name on Record
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.name}
                      </div>
                    </div>
                  )}

                  {(meta.dob || meta.dateOfBirth) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Date of Birth
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.dob || meta.dateOfBirth}
                      </div>
                    </div>
                  )}

                  {meta.achievements && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 sm:col-span-2">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Achievements & Honors
                      </div>
                      <div className="text-slate-800 font-medium mt-1">
                        {meta.achievements}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Work Specifics */}
              {isWork && (
                <>
                  {meta.employmentType && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Employment Type
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.employmentType}
                      </div>
                    </div>
                  )}

                  {meta.department && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Department
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.department}
                      </div>
                    </div>
                  )}

                  {meta.location && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Location / City
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.location}
                      </div>
                    </div>
                  )}

                  {(meta.branchName || c.branch?.name) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Branch / Office
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.branchName || c.branch?.name}
                      </div>
                    </div>
                  )}

                  {(meta.reportingManagerName ||
                    meta.reportingManagerDesignation) && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Reporting Manager
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {meta.reportingManagerName || "N/A"}{" "}
                        {meta.reportingManagerDesignation && (
                          <span className="text-slate-500 font-normal">
                            ({meta.reportingManagerDesignation})
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {meta.ctc != null && meta.ctc !== "" && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Annual CTC
                      </div>
                      <div className="text-slate-800 font-semibold mt-1">
                        {!isNaN(Number(meta.ctc))
                          ? `₹${Number(meta.ctc).toLocaleString("en-IN")}`
                          : meta.ctc}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Other / Certification Specifics */}
              {isOther && (
                <>
                  {meta.credentialId && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Credential ID / License No.
                      </div>
                      <div className="text-slate-800 font-semibold mt-1 font-mono">
                        {meta.credentialId}
                      </div>
                    </div>
                  )}

                  {meta.url && (
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 sm:col-span-2">
                      <div className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                        Verification URL
                      </div>
                      <a
                        href={
                          meta.url.startsWith("http")
                            ? meta.url
                            : `https://${meta.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 hover:underline font-semibold mt-1 inline-flex items-center gap-1.5 break-all"
                      >
                        <span>{meta.url}</span>
                        <InlineIcon name="external" size={12} />
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Description Block */}
          {c.description && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-xs space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-[.06em] text-slate-400">
                Description / Scope
              </h4>
              <p className="text-xs sm:text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {c.description}
              </p>
            </div>
          )}

          {/* Certificate / Proof Document Section */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-xs space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-[.06em] text-slate-400">
              Attached Proof Document
            </h4>
            {c.certificateS3Key ? (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-800 truncate">
                      {c.certificateFileName || "Proof Document"}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Uploaded proof file
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleOpenCertificate}
                  disabled={openingDoc}
                  className="px-3.5 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition shadow-xs flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <InlineIcon name="external" size={12} />
                  {openingDoc ? "Opening..." : "View Document"}
                </button>
              </div>
            ) : (
              <div className="text-xs text-slate-500 bg-slate-50 p-3.5 rounded-xl border border-slate-100 italic">
                No certificate or proof document attached to this record.
              </div>
            )}
            {docError && (
              <div className="text-xs text-red-600 font-medium">{docError}</div>
            )}
          </div>

          {/* Verification Status Banner */}
          <div
            className={`rounded-2xl p-4 border text-xs leading-relaxed ${
              c.status === "VERIFIED"
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : c.status === "PENDING"
                  ? "bg-amber-50 border-amber-200 text-amber-800"
                  : c.status === "REJECTED"
                    ? "bg-red-50 border-red-200 text-red-800"
                    : "bg-slate-50 border-slate-200 text-slate-700"
            }`}
          >
            <div className="font-bold text-sm mb-1.5 flex items-center gap-2">
              <StatusBadge status={c.status} />
              <span>
                {c.status === "VERIFIED"
                  ? "Official Verified Credential"
                  : c.status === "PENDING"
                    ? "Verification In Progress"
                    : c.status === "REJECTED"
                      ? "Verification Rejected"
                      : "Self-Reported Record (Unverified)"}
              </span>
            </div>
            <div>
              {c.status === "VERIFIED" && (
                <div className="space-y-2">
                  <p>
                    This record has been officially verified by the issuing
                    institution or employer. It carries a cryptographic
                    verification badge.
                  </p>
                  {(verifierName || formattedDecidedAt) && (
                    <div className="pt-2 border-t border-emerald-200/70 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-medium text-emerald-900">
                      {verifierName && (
                        <div>
                          <span className="text-emerald-700 font-normal">
                            Verified by:{" "}
                          </span>
                          <span className="font-semibold">{verifierName}</span>
                        </div>
                      )}
                      {formattedDecidedAt && (
                        <div>
                          <span className="text-emerald-700 font-normal">
                            Decision Date:{" "}
                          </span>
                          <span className="font-semibold">
                            {formattedDecidedAt}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {editPending && (
                    <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px]">
                      <span className="font-bold">
                        Pending Change Request:{" "}
                      </span>
                      A request to update verified details was submitted and is
                      awaiting verifier review.
                    </div>
                  )}
                </div>
              )}
              {c.status === "PENDING" && (
                <div className="space-y-2">
                  <p>
                    A verification request has been dispatched to the verifier
                    and is currently pending review.
                  </p>
                  {verifierName && (
                    <div className="pt-2 border-t border-amber-200/70 text-[11px] font-medium text-amber-900">
                      <span className="text-amber-700 font-normal">
                        Verifier:{" "}
                      </span>
                      <span className="font-semibold">{verifierName}</span>
                    </div>
                  )}
                </div>
              )}
              {c.status === "REJECTED" && (
                <div className="space-y-2">
                  <p>
                    The verifier declined to verify this credential. You can
                    edit the details and request verification again.
                  </p>
                  {(verifierName || formattedDecidedAt || decisionReason) && (
                    <div className="pt-2 border-t border-red-200/70 space-y-1.5 text-[11px] font-medium text-red-900">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {verifierName && (
                          <div>
                            <span className="text-red-700 font-normal">
                              Decided by:{" "}
                            </span>
                            <span className="font-semibold">
                              {verifierName}
                            </span>
                          </div>
                        )}
                        {formattedDecidedAt && (
                          <div>
                            <span className="text-red-700 font-normal">
                              Decision Date:{" "}
                            </span>
                            <span className="font-semibold">
                              {formattedDecidedAt}
                            </span>
                          </div>
                        )}
                      </div>
                      {decisionReason && (
                        <div>
                          <span className="text-red-700 font-normal">
                            Reason:{" "}
                          </span>
                          <span className="font-semibold">
                            {decisionReason}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {c.status === "NONE" &&
                "This credential is self-reported and unverified. You can request official verification from the issuer anytime."}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3.5 sm:px-6 sm:py-4 border-t border-slate-200 bg-slate-50/90 flex flex-wrap items-center justify-between gap-2.5 shrink-0">
          <div>
            {(c.status === "NONE" || c.status === "REJECTED") && (
              <Link
                to={`/verify/request?credentialId=${c.id}`}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition shadow-xs inline-flex items-center gap-1.5"
              >
                Request Verification
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2.5 ml-auto">
            {viewRoute && (
              <Link
                to={viewRoute}
                className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition shadow-xs inline-flex items-center gap-1.5"
                title="View in full form layout"
              >
                <span>Form View</span>
              </Link>
            )}
            {editLink && (
              <Link
                to={editLink}
                className="px-4 py-2 rounded-xl bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold border border-slate-300 transition shadow-xs inline-flex items-center gap-1.5"
              >
                {c.status === "VERIFIED"
                  ? "Edit Additional Info"
                  : "Edit Details"}
              </Link>
            )}
            {changeable && editLink && (
              <Link
                to={`${editLink}?change=1`}
                className="px-4 py-2 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-bold border border-purple-200 transition shadow-xs inline-flex items-center gap-1.5"
                title="Change verified details (fee applies)"
              >
                Change Verified Details
              </Link>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold transition shadow-xs cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
}
